// Phase 8.7 — LiftedAtTheMoment Storefront Overview
// Real-data KPIs for the LATM commerce surface.

import React, { useEffect, useState } from "react";
import { DollarSign, ShoppingBag, Users, Package, AlertCircle, TrendingUp } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminFeatureShell from "@/components/admin/AdminFeatureShell";

function dollars(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="glass-panel p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
        <Icon className="h-3.5 w-3.5 text-lifted-green" /> {label}
      </div>
      <div className="font-anton text-3xl text-white mt-2">{value}</div>
      {sub && <div className="text-[0.65rem] text-lifted-dim mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminStorefrontOverview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/storefront/overview");
        setData(data);
      } catch (e) { setErr(apiErrorMessage(e)); }
    })();
  }, []);

  return (
    <AdminFeatureShell
      breadcrumb="LiftedAtTheMoment / Storefront"
      title="Storefront Overview"
      description="Top-line storefront performance — real revenue from native Lifted Core orders."
      status={data ? `${data.kpis?.orders_total ?? 0} total orders` : null}
      testId="admin-storefront-overview"
    >
      {err && (
        <div className="glass-panel border border-rose-500/30 p-3 text-rose-200 text-sm" data-testid="overview-error">{err}</div>
      )}
      {!data && !err && <div className="text-sm text-lifted-dim">Loading…</div>}
      {data && (
        <>
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="overview-kpi-grid">
            <Kpi icon={DollarSign} label="Revenue (30d)"
                 value={dollars(data.kpis.revenue_cents_30d)}
                 sub={`${data.kpis.orders_30d} paid orders · ${dollars(data.kpis.avg_order_cents_30d)} avg`}
                 testid="kpi-revenue-30d" />
            <Kpi icon={DollarSign} label="Revenue (7d)"
                 value={dollars(data.kpis.revenue_cents_7d)}
                 sub={`${data.kpis.orders_7d} orders`}
                 testid="kpi-revenue-7d" />
            <Kpi icon={ShoppingBag} label="Orders pending"
                 value={data.kpis.orders_pending}
                 sub="Awaiting payment confirmation"
                 testid="kpi-orders-pending" />
            <Kpi icon={Users} label="Customers"
                 value={data.customers.total}
                 sub={`+${data.customers.new_30d} new in 30 days`}
                 testid="kpi-customers" />
          </section>

          <section className="grid lg:grid-cols-2 gap-4">
            <div className="glass-panel p-5" data-testid="overview-catalog">
              <div className="flex items-center gap-2 mb-3 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
                <Package className="h-3.5 w-3.5 text-lifted-green" /> CATALOG HEALTH
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-lifted-dim">Total products</span><div className="text-2xl font-anton">{data.catalog.total}</div></div>
                <div><span className="text-lifted-dim">Active</span><div className="text-2xl font-anton text-lifted-green">{data.catalog.active}</div></div>
                <div><span className="text-lifted-dim">Missing price</span>
                  <div className={`text-2xl font-anton ${data.catalog.missing_price > 0 ? "text-amber-300" : "text-lifted-green"}`}>
                    {data.catalog.missing_price}
                  </div>
                </div>
                <div><span className="text-lifted-dim">Missing image</span>
                  <div className={`text-2xl font-anton ${data.catalog.missing_image > 0 ? "text-amber-300" : "text-lifted-green"}`}>
                    {data.catalog.missing_image}
                  </div>
                </div>
              </div>
              {(data.catalog.missing_price > 0 || data.catalog.missing_image > 0) && (
                <div className="mt-3 text-[0.65rem] text-amber-300 inline-flex items-center gap-1.5">
                  <AlertCircle className="h-3 w-3" /> Some products need attention in Products & Inventory.
                </div>
              )}
            </div>

            <div className="glass-panel p-5" data-testid="overview-top-skus">
              <div className="flex items-center gap-2 mb-3 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
                <TrendingUp className="h-3.5 w-3.5 text-lifted-green" /> TOP SKUS (30d)
              </div>
              {data.top_skus_30d.length === 0 ? (
                <div className="text-xs text-lifted-dim">No paid orders in the last 30 days yet.</div>
              ) : (
                <ul className="space-y-1.5 text-xs">
                  {data.top_skus_30d.map((s) => (
                    <li key={s.handle} className="flex items-center justify-between gap-2">
                      <span className="truncate text-lifted-text">{s.title || s.handle}</span>
                      <span className="text-lifted-dim">{s.qty}× · {dollars(s.revenue_cents)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Kpi icon={DollarSign} label="Revenue (all time)" value={dollars(data.kpis.revenue_cents_all)}
                 sub={`${data.kpis.orders_paid} paid orders`} testid="kpi-revenue-all" />
            <Kpi icon={ShoppingBag} label="Total orders" value={data.kpis.orders_total}
                 sub={`${data.kpis.orders_paid} paid · ${data.kpis.orders_pending} pending`} testid="kpi-orders-total" />
            <Kpi icon={Users} label="Customers (30d)" value={data.customers.new_30d}
                 sub="New sign-ups" testid="kpi-customers-30d" />
          </section>
        </>
      )}
    </AdminFeatureShell>
  );
}
